import React, { useEffect, useMemo, useRef, useState } from "react";
import i18next from "i18next";
import { humanOrderNo } from "../utils/orderNo";
import { useTranslation } from "react-i18next";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";

// REISOR_STAGE22P1E_FINAL_RUNTIME_I18N_TAIL_CLEANUP_V1

const CURRENCIES = ["RUB", "USD", "EUR", "UZS"];
const TOPUP_CURRENCIES = ["RUB", "UZS"];
const SUBSCRIPTION_COUNTRIES = ["RU", "UZ"]; // REISOR_STAGE22D_DRIVER_SUBSCRIPTION_CARD_AUTOPAY_V1

function toBearer(token) {
  if (!token) return "";
  const t = String(token);
  return t.startsWith("Bearer ") ? t : `Bearer ${t}`;
}

function normalizeCurrency(v) {
  const c = String(v || "RUB").toUpperCase().trim();
  return CURRENCIES.includes(c) ? c : "RUB";
}

function fmtMoney(v, currency = "RUB", opts = {}) {
  const n = Number(v || 0);
  const sign = opts.signed ? (n < 0 ? "-" : n > 0 ? "+" : "") : (n < 0 ? "-" : "");
  const abs = Math.abs(n);
  const cur = normalizeCurrency(currency);
  const digits = cur === "UZS" || cur === "RUB" ? 0 : 2;
  const value = abs.toLocaleString("ru-RU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  const suffix = cur === "RUB" ? "₽" : cur === "USD" ? "$" : cur === "EUR" ? "€" : "UZS";
  return `${sign}${value} ${suffix}`;
}

function fmtDT(v) {
  try {
    const d = new Date(v);
    const date = d.toLocaleDateString("ru-RU", { year: "numeric", month: "2-digit", day: "2-digit" });
    const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    return `${date} ${time}`;
  } catch {
    return "—";
  }
}

// REISOR_STAGE22D_DRIVER_SUBSCRIPTION_CARD_AUTOPAY_V1
function fmtDateOnly(v) {
  try {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("ru-RU", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return "—";
  }
}

function subscriptionCountryLabel(country, t) {
  const c = String(country || "RU").toUpperCase();
  return c === "UZ"
    ? t("driverSubscription.cardCountryUz")
    : t("driverSubscription.cardCountryRu");
}

function normalizeSubscriptionCountry(v) {
  const c = String(v || "RU").toUpperCase();
  return c === "UZ" ? "UZ" : "RU";
}

function maskCardForUi(v) {
  const d = String(v || "").replace(/\D/g, "");
  if (!d) return "";
  return d.length <= 4 ? d : `${d.slice(0, 4)} ${d.slice(4, 8)} ${d.slice(8, 12)} ${d.slice(12)}`.trim();
}

function txLabel(tx) {
  const type = String(tx?.type || "").toUpperCase();

  const keyByType = {
    DRIVER_COMMISSION: "balance.tx.fee",
    COMMISSION: "balance.tx.fee",
    DRIVER_PAYOUT: "balance.tx.payout",
    ORDER_PAYOUT: "balance.tx.payout",
    CASH_TOPUP: "balance.tx.topup",
    TOPUP: "balance.tx.topup",
    CASH_WITHDRAW: "balance.tx.withdraw",
    WITHDRAW: "balance.tx.withdraw",
    ADJUSTMENT: "balance.tx.adjustment",
  };

  const k = keyByType[type];
  if (k) return i18next.t(k, { defaultValue: type || "—" });

  return type || i18next.t("balance.tx.unknown", { defaultValue: "—" });
}

function normalizeBalances(raw, fallbackRub = 0) {
  const out = { RUB: 0, USD: 0, EUR: 0, UZS: 0 };
  const src = raw && typeof raw === "object" ? raw : {};
  for (const c of CURRENCIES) {
    const n = Number(src[c]);
    out[c] = Number.isFinite(n) ? n : 0;
  }
  if (!raw || typeof raw !== "object") out.RUB = Number(fallbackRub || 0);
  return out;
}

function statusText(s, t) {
  const v = String(s || "pending").toLowerCase();
  if (v === "approved") return t("balance.topup.status.approved");
  if (v === "rejected") return t("balance.topup.status.rejected");
  return t("balance.topup.status.pending");
}

function statusClass(s) {
  const v = String(s || "pending").toLowerCase();
  if (v === "approved" || v === "activated") return "text-emerald-200 border-emerald-700/50 bg-emerald-900/25";
  if (v === "rejected" || v === "reversed") return "text-red-200 border-red-700/50 bg-red-900/25";
  return "text-amber-200 border-amber-700/50 bg-amber-900/25";
}

function subscriptionPaymentStatusText(s, t) {
  const v = String(s || "pending").toLowerCase();
  if (v === "approved" || v === "activated") return t("driverSubscription.statusApproved");
  if (v === "rejected") return t("driverSubscription.statusRejected");
  if (v === "reversed") return t("driverSubscription.statusReversed");
  return t("driverSubscription.statusPending");
}

// REISOR_STAGE22E_DRIVER_SUBSCRIPTION_DISPETCHER_CONFIRM_V1
function fmtSubscriptionPaymentRow(p, t) {
  const amount = Number(p?.amount || 0);
  const currency = String(p?.currency || "RUB").toUpperCase();
  const digits = currency === "UZS" || currency === "RUB" ? 0 : 2;
  const formatted = `${amount.toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${currency === "RUB" ? "₽" : currency === "UZS" ? "UZS" : currency}`;
  return `${formatted} · ${subscriptionPaymentStatusText(p?.status, t)}`;
}

function methodText(v, t) {
  const s = String(v || "transfer");
  const map = {
    sbp: "balance.topup.method.sbp",
    card: "balance.topup.method.card",
    bank: "balance.topup.method.bank",
    transfer: "balance.topup.method.transfer",
    cash: "balance.topup.method.cash",
    other: "balance.topup.method.other",
  };
  return map[s] ? t(map[s]) : s;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function DriverBalance({ driverToken, onOpenOrder }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const paymentCountryRef = useRef(null);
  const [balance, setBalance] = useState(0);
  const [balances, setBalances] = useState({ RUB: 0, USD: 0, EUR: 0, UZS: 0 });
  const [fx, setFx] = useState(null);
  const [items, setItems] = useState([]);
  const [topupRequests, setTopupRequests] = useState([]);

  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupCurrency, setTopupCurrency] = useState("RUB");
  const [topupMethod, setTopupMethod] = useState("transfer");
  const [topupComment, setTopupComment] = useState("");
  const [topupReceipt, setTopupReceipt] = useState("");
  const [topupSubmitting, setTopupSubmitting] = useState(false);

  // REISOR_STAGE22D_DRIVER_SUBSCRIPTION_CARD_AUTOPAY_V1
  const [subscription, setSubscription] = useState(null);
  const [clickUzTariffs, setClickUzTariffs] = useState(null);
  const [subscriptionCountry, setSubscriptionCountry] = useState("");
  const [subscriptionSubmitting, setSubscriptionSubmitting] = useState(false);
  const [subscriptionCopied, setSubscriptionCopied] = useState("");

  const headers = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (driverToken) h.Authorization = toBearer(driverToken);
    return h;
  }, [driverToken]);

  // REISOR_STAGE22D_DRIVER_SUBSCRIPTION_CARD_AUTOPAY_V1
  const loadSubscriptionStatus = async (countryOverride = subscriptionCountry) => {
    if (!driverToken) return;
    const country = normalizeSubscriptionCountry(countryOverride);
    try {
      const res = await fetch(`/api/driver/subscription/status?country=${encodeURIComponent(country)}`, {
        headers,
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        if (country === "UZ") {
          try {
            const tariffResponse = await fetch(
              "/api/driver/subscription/click/tariffs",
              {
                headers,
                credentials: "include",
                cache: "no-store",
              }
            );

            const tariffData =
              await tariffResponse
                .json()
                .catch(() => ({}));

            if (
              tariffResponse.ok &&
              tariffData?.ok
            ) {
              setClickUzTariffs(tariffData);
            }
          } catch {}
        }

        setSubscription(data);
        
      }
    } catch {}
  };

  const loadTopupRequests = async () => {
    if (!driverToken) return;
    try {
      const res = await fetch("/api/driver/topup-requests?limit=20", {
        headers,
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) setTopupRequests(Array.isArray(data.items) ? data.items : []);
    } catch {}
  };

  const load = async () => {
    if (!driverToken) {
      setErr(t("errors.no_driver_token_balance"));
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/driver/wallet?limit=80", {
        headers,
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setErr(data?.error || t("errors.balance_load_failed"));
        return;
      }
      const nextBalances = normalizeBalances(data.balancesByCurrency || data.balances, data.balance);
      setBalance(Number(data.balanceRub ?? data.balance ?? nextBalances.RUB ?? 0));
      setBalances(nextBalances);
      setFx(data.fx || null);
      setItems(Array.isArray(data.items) ? data.items : []);
      await loadTopupRequests();
      await loadSubscriptionStatus();
    } catch {
      setErr(t("errors.network"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [driverToken]);

  const submitTopup = async () => {
    const amount = Number(String(topupAmount || "").replace(",", "."));
    const currency = TOPUP_CURRENCIES.includes(String(topupCurrency).toUpperCase()) ? String(topupCurrency).toUpperCase() : "RUB";
    if (!(amount > 0)) {
      setErr(t("balance.topup.amountRequired"));
      return;
    }

    setTopupSubmitting(true);
    setErr("");
    try {
      const res = await fetch("/api/driver/topup-requests", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          amount,
          currency,
          paymentMethod: topupMethod,
          comment: topupComment,
          receiptDataUrl: topupReceipt,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setErr(data?.error || t("balance.topup.submitFailed"));
        return;
      }

      setTopupOpen(false);
      setTopupAmount("");
      setTopupCurrency("RUB");
      setTopupMethod("transfer");
      setTopupComment("");
      setTopupReceipt("");
      await loadTopupRequests();
      // eslint-disable-next-line no-alert
      alert(t("balance.topup.submitted"));
    } catch {
      setErr(t("errors.network"));
    } finally {
      setTopupSubmitting(false);
    }
  };

  // REISOR_CLICK_UZ_DYNAMIC_PRICE_DRIVER_V1
  const clickUzTariffRows = Array.isArray(clickUzTariffs?.tariffs)
    ? clickUzTariffs.tariffs
    : [];

  const clickUzPriceFor = (code) => {
    const row = clickUzTariffRows.find(
      (item) => String(item?.code || "") === code
    );
    const amount = Number(row?.amount || 0);

    if (!(amount > 0)) return null;

    return {
      amount,
      currency: "UZS",
      label: `${amount.toLocaleString("ru-RU")} UZS`,
    };
  };

  const clickUzBasePrice = clickUzPriceFor(
    "driver_base_access_30d"
  );
  // REISOR_STAGE22D_DRIVER_SUBSCRIPTION_CARD_AUTOPAY_V1
  const subscriptionPrice = subscriptionCountry === "UZ"
    ? (
      clickUzBasePrice ||
      subscription?.price ||
      {
        amount: 30000,
        currency: "UZS",
        label: t("driverSubscription.priceUz"),
      }
    )
    : (
      subscription?.price ||
      {
        amount: 199,
        currency: "RUB",
        label: t("driverSubscription.priceRu"),
      }
    );

  const subscriptionPromo = subscriptionCountry === "UZ"
    ? {
        oldLabel: t("driverSubscription.oldPriceUz"),
        discountLabel: t("driverSubscription.discountUz"),
      }
    : {
        oldLabel: t("driverSubscription.oldPriceRu"),
        discountLabel: t("driverSubscription.discountRu"),
      };

  const subscriptionRequisites = subscription?.requisites || {};
  const subscriptionAccess = subscription?.access || {};
  const subscriptionActive = Boolean(subscriptionAccess?.hasActiveSubscription);
  const subscriptionUntil = subscriptionAccess?.subscriptionUntil || "";
  const subscriptionCardNumber = String(subscriptionRequisites?.cardNumber || "");
  const subscriptionRequisitesReady = Boolean(subscriptionRequisites?.configured && subscriptionCardNumber);
  const subscriptionPayments = Array.isArray(subscription?.lastPayments) ? subscription.lastPayments : [];
  const pendingSubscriptionPayments = subscriptionPayments.filter((p) => String(p?.status || "").toLowerCase() === "pending");
  const hasPendingSubscriptionPayment = pendingSubscriptionPayments.length > 0;

  // REISOR_BILLING_UI_V1_DRIVER_TARIFFS_STUB
  const billingV1 = subscription?.billingV1 || {};

  const baseTariffSource = billingV1?.baseAccess || {
    title: t("driverSubscription.baseTitle"),
    active: subscriptionActive,
    until: subscriptionUntil || null,
    price: subscriptionCountry === "UZ"
      ? { label: t("driverSubscription.priceUz") }
      : { label: t("driverSubscription.priceRu") },
  };

  const baseTariff =
    subscriptionCountry === "UZ" &&
    clickUzBasePrice
      ? {
          ...baseTariffSource,
          price: clickUzBasePrice,
        }
      : baseTariffSource;

  // REISOR_DRIVER_LEGACY_CONTACTS_UI_HIDDEN_V1
  // Legacy driver_client_contacts_30d remains backend-compatible for historical
  // payments/data, but it is no longer a public Driver product.

  const subscriptionPaymentText = () => {
    const parts = [
      `REISOR: ${subscriptionPrice?.label || ""}`,
      subscriptionRequisites?.bankName ? `${t("driverSubscription.bankLabel")} ${subscriptionRequisites.bankName}` : "",
      subscriptionRequisites?.recipient ? `${t("driverSubscription.recipientLabel")} ${subscriptionRequisites.recipient}` : "",
      subscriptionCardNumber ? `${t("driverSubscription.cardLabel")} ${subscriptionCardNumber}` : "",
      subscriptionRequisites?.comment ? `${t("driverSubscription.commentLabel")} ${subscriptionRequisites.comment}` : "",
    ].filter(Boolean);
    return parts.join("\n");
  };

  const copySubscriptionPayment = async (kind = "all") => {
    const text = kind === "card" ? subscriptionCardNumber : subscriptionPaymentText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setSubscriptionCopied(kind);
      setTimeout(() => setSubscriptionCopied(""), 1800);
    } catch {
      setErr(t("driverSubscription.copyFailed"));
    }
  };

  // REISOR_STAGE22E_DRIVER_SUBSCRIPTION_DISPETCHER_CONFIRM_V1
  const activateSubscription = async () => {
    if (!subscriptionRequisitesReady) {
      setErr(t("driverSubscription.requisitesMissing"));
      return;
    }
    if (hasPendingSubscriptionPayment) {
      setErr(t("driverSubscription.alreadyPending"));
      return;
    }
    setSubscriptionSubmitting(true);
    setErr("");
    try {
      const res = await fetch("/api/driver/subscription/activate", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ country: subscriptionCountry }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setErr(data?.message || data?.error || t("driverSubscription.submitFailed"));
        if (data?.lastPayments || data?.pendingPayment) setSubscription(data);
        return;
      }
      setSubscription(data);
      // eslint-disable-next-line no-alert
      alert(t("driverSubscription.submitted"));
    } catch {
      setErr(t("errors.network"));
    } finally {
      setSubscriptionSubmitting(false);
    }
  };

  // REISOR_TBANK_DRIVER_FRONTEND_PAYMENT_BUTTON_V1
  const startTbankPayment = async (tariffCode) => {
    const country = normalizeSubscriptionCountry(subscriptionCountry);

    if (country !== "RU") {
      setErr(t("driverSubscription.tbankRuOnly", {
        defaultValue: "Онлайн-оплата банковской картой сейчас доступна только для России.",
      }));
      return;
    }

    if (!driverToken) {
      setErr(t("errors.no_driver_token_balance"));
      return;
    }

    const code = String(tariffCode || "").trim();
    if (!code) return;

    setSubscriptionSubmitting(true);
    setErr("");

    try {
      const res = await fetch("/api/driver/subscription/tbank/init", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          country: "RU",
          tariffCode: code,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        setErr(data?.message || data?.error || t("driverSubscription.submitFailed"));
        return;
      }

      const paymentUrl = String(
        data?.paymentUrl ||
        data?.PaymentURL ||
        data?.paymentURL ||
        data?.payUrl ||
        data?.url ||
        ""
      ).trim();

      if (!paymentUrl) {
        setErr(t("driverSubscription.paymentUrlMissing", {
          defaultValue: "Банк не вернул ссылку на оплату. Попробуйте позже.",
        }));
        return;
      }

      window.location.assign(paymentUrl);
    } catch {
      setErr(t("errors.network"));
    } finally {
      setSubscriptionSubmitting(false);
    }
  };

  // REISOR_CLICK_UZ_DRIVER_FRONTEND_PAYMENT_V1
  const startClickPayment = async (tariffCode) => {
    const country = normalizeSubscriptionCountry(
      subscriptionCountry
    );

    if (country !== "UZ") {
      setErr(
        t("driverSubscription.clickUzOnly")
      );
      return;
    }

    if (!driverToken) {
      setErr(t("errors.no_driver_token_balance"));
      return;
    }

    const code = String(tariffCode || "").trim();
    if (!code) return;

    setSubscriptionSubmitting(true);
    setErr("");

    try {
      const res = await fetch(
        "/api/driver/subscription/click/init",
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({
            country: "UZ",
            tariffCode: code,
          }),
        }
      );

      const data = await res
        .json()
        .catch(() => ({}));

      if (!res.ok || !data?.ok) {
        const errorKeyByCode = {
          CLICK_UZ_DISABLED:
            "driverSubscription.clickDisabled",
          CLICK_UZ_NOT_CONFIGURED:
            "driverSubscription.clickNotConfigured",
          CLICK_UZ_FISCAL_NOT_READY:
            "driverSubscription.clickFiscalNotReady",
          CLICK_UZ_UZS_ONLY:
            "driverSubscription.clickUzOnly",
          CLICK_UZ_TARIFF_INVALID:
            "driverSubscription.clickTariffInvalid",
          CLICK_UZ_INIT_FAILED:
            "driverSubscription.clickInitFailed",
          BASE_ACCESS_REQUIRED:
            "driverSubscription.contactsNeedBase",
        };

        const errorKey =
          errorKeyByCode[
            String(data?.error || "")
          ];

        setErr(
          errorKey
            ? t(errorKey)
            : t(
                "driverSubscription.clickInitFailed"
              )
        );
        return;
      }

      const paymentUrl = String(
        data?.paymentUrl ||
          data?.PaymentURL ||
          data?.paymentURL ||
          data?.payUrl ||
          data?.url ||
          ""
      ).trim();

      if (!paymentUrl) {
        setErr(
          t(
            "driverSubscription.paymentUrlMissing"
          )
        );
        return;
      }

      let paymentTarget;

      try {
        paymentTarget = new URL(paymentUrl);
      } catch {
        setErr(
          t(
            "driverSubscription.clickUrlInvalid"
          )
        );
        return;
      }

      const paymentHost =
        paymentTarget.hostname.toLowerCase();

      const trustedClickHost =
        paymentHost === "click.uz" ||
        paymentHost.endsWith(".click.uz");

      if (
        paymentTarget.protocol !== "https:" ||
        !trustedClickHost
      ) {
        setErr(
          t(
            "driverSubscription.clickUrlInvalid"
          )
        );
        return;
      }

      window.location.assign(
        paymentTarget.toString()
      );
    } catch {
      setErr(t("errors.network"));
    } finally {
      setSubscriptionSubmitting(false);
    }
  };

  const paymentCountryRequiredMessage = t(
    "driverSubscription.paymentCountryRequired"
  );
  const paymentCountryError =
    !subscriptionCountry &&
    err === paymentCountryRequiredMessage;

  const startSubscriptionPayment = async (
    tariffCode
  ) => {
    if (!subscriptionCountry) {
      setErr(paymentCountryRequiredMessage);

      window.requestAnimationFrame(() => {
        paymentCountryRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });

      return;
    }

    const country = normalizeSubscriptionCountry(
      subscriptionCountry
    );

    if (country === "UZ") {
      return startClickPayment(tariffCode);
    }

    return startTbankPayment(tariffCode);
  };

  if (topupOpen) {
    return (
      <div className="w-full">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setTopupOpen(false)}
            className="rounded-xl border border-slate-700 bg-slate-800/40 px-3 py-2 text-sm font-semibold"
          >
            ←
          </button>
          <div className="text-xl font-black">{t("balance.topup.title")}</div>
        </div>

        {err ? (
          <div className="mt-3 rounded-2xl border border-red-700/40 bg-red-900/20 p-3 text-red-100">
            {err}
          </div>
        ) : null}

        <div className="mt-4 rounded-3xl border border-slate-700 bg-slate-900/60 p-4">
          <div className="text-sm font-bold">{t("balance.topup.currencyTitle")}</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {TOPUP_CURRENCIES.map((cur) => (
              <button
                key={cur}
                type="button"
                onClick={() => setTopupCurrency(cur)}
                className={`rounded-2xl border px-3 py-3 text-left ${
                  topupCurrency === cur
                    ? "border-sky-500 bg-sky-900/30"
                    : "border-slate-700 bg-slate-950/35"
                }`}
              >
                <div className="text-xs text-slate-400">{cur}</div>
                <div className="mt-1 text-lg font-black">{cur === "RUB" ? "₽" : "UZS"}</div>
              </button>
            ))}
          </div>

          <label className="mt-4 block text-sm font-bold">{t("balance.topup.amountLabel")}</label>
          <input
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
            inputMode="decimal"
            placeholder={topupCurrency === "UZS" ? t("balance.topup.exampleUz") : t("balance.topup.exampleRu")}
            className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-lg font-bold outline-none"
          />

          <label className="mt-4 block text-sm font-bold">{t("balance.topup.methodLabel")}</label>
          {/* REISOR_DRIVER_BALANCE_TOPUP_METHOD_NATIVE_SAFE_SELECT_V1 */}
            <NativeSafeSelect
              value={topupMethod}
              onChange={setTopupMethod}
              className="mt-2 w-full"
              buttonClassName="w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-left text-base font-semibold outline-none"
              menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
              ariaLabel={t("balance.topup.methodLabel")}
              options={[
                { value: "transfer", label: t("balance.topup.method.transfer") },
                { value: "card", label: t("balance.topup.method.card") },
                { value: "sbp", label: t("balance.topup.method.sbp") },
                { value: "bank", label: t("balance.topup.method.bank") },
                { value: "other", label: t("balance.topup.method.other") },
              ]}
            />

          <label className="mt-4 block text-sm font-bold">{t("balance.topup.receiptLabel")}</label>
          <input
            type="file"
            accept="image/*"
            className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-sm"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) {
                setTopupReceipt("");
                return;
              }
              try {
                const dataUrl = await fileToDataUrl(file);
                setTopupReceipt(dataUrl);
              } catch {
                setErr(t("balance.topup.fileReadFailed"));
              }
            }}
          />
          {topupReceipt ? <div className="mt-2 text-xs text-emerald-200">{t("balance.topup.receiptAdded")}</div> : null}

          <label className="mt-4 block text-sm font-bold">{t("balance.topup.commentLabel")}</label>
          <textarea
            value={topupComment}
            onChange={(e) => setTopupComment(e.target.value)}
            rows={3}
            placeholder={t("balance.topup.commentPlaceholder")}
            className="mt-2 w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-sm outline-none"
          />

          <button
            type="button"
            disabled={topupSubmitting}
            onClick={submitTopup}
            className="mt-4 w-full rounded-2xl bg-sky-600 px-4 py-3 text-base font-black text-white disabled:opacity-60"
          >
            {topupSubmitting ? t("balance.topup.submitting") : t("balance.topup.submit")}
          </button>

          <div className="mt-3 text-xs text-slate-300/75">
            {t("balance.topup.confirmationHint")}
          </div>
        </div>

        <div className="h-6" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
          <div className="text-xl font-black">{t("driverSubscription.billingTitle", { defaultValue: "Тарифы REISOR" })}</div>
        <button
          type="button"
          onClick={load}
          className="rounded-xl border border-slate-700 bg-slate-800/40 px-3 py-2 text-sm font-semibold"
          disabled={loading}
        >
          {loading ? t("common.updating") : t("common.refresh")}
        </button>
      </div>

      {err && !paymentCountryError ? (
        <div className="mt-3 rounded-2xl border border-red-700/40 bg-red-900/20 p-3 text-red-100">
          {err}
        </div>
      ) : null}

      {/* REISOR_BILLING_UI_V1_DRIVER_TARIFFS_STUB */}
      <div className="mt-3 rounded-3xl border border-emerald-700/45 bg-slate-900/60 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-emerald-200/80">
              {t("driverSubscription.billingTitle")}
            </div>
            <div className="mt-1 text-lg font-black">
              {t("driverSubscription.billingSubtitle")}
            </div>
            <div className="mt-1 text-xs leading-5 text-slate-300/80">
              {t("driverSubscription.billingModelHint")}
            </div>
          </div>
          <div className="rounded-xl border border-emerald-700/40 bg-emerald-900/20 px-3 py-2 text-right text-xs font-bold text-emerald-100">
            30 {t("driverSubscription.days")}
          </div>
        </div>

        <div
          ref={paymentCountryRef}
          className="mt-4 scroll-mt-24 text-sm font-semibold text-slate-300"
        >
          {t("driverSubscription.paymentCountryTitle")}
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2">
          {SUBSCRIPTION_COUNTRIES.map((country) => (
            <button
              key={country}
              type="button"
              onClick={() => {
                setErr("");
                setSubscriptionCountry(country);
                loadSubscriptionStatus(country);
              }}
              className={`rounded-2xl border px-3 py-3 text-center transition-colors ${
                subscriptionCountry === country
                  ? "border-emerald-700/70 bg-emerald-950/55"
                  : paymentCountryError
                    ? "border-red-600/75 bg-red-950/25"
                    : "border-slate-700/80 bg-slate-950/70"
              }`}
            >
              <div className="text-sm font-bold text-slate-100">
                {subscriptionCountryLabel(country, t)}
              </div>
            </button>
          ))}
        </div>

        {paymentCountryError ? (
          <div
            role="alert"
            aria-live="assertive"
            className="mt-2 rounded-2xl border border-red-700/55 bg-red-950/35 p-3 text-sm font-semibold text-red-100"
          >
            {err}
          </div>
        ) : null}

        <div className="mt-4 grid gap-3">
          <div className="rounded-2xl border border-slate-700 bg-slate-950/35 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-black text-slate-100">
                  {/* REISOR_DRIVER_BILLING_TARIFF_TITLE_I18N_V2 */}
                  {t("driverSubscription.baseTitle")}
                </div>
                <div className="mt-1 text-xs leading-5 text-slate-300/80">
                  {t("driverSubscription.baseDesc")}
                </div>
              </div>
              <div className={`shrink-0 rounded-xl border px-2 py-1 text-xs font-bold ${
                baseTariff?.active
                  ? "border-emerald-600/50 bg-emerald-900/25 text-emerald-100"
                  : "border-amber-600/50 bg-amber-900/25 text-amber-100"
              }`}>
                {baseTariff?.active ? t("driverSubscription.statusActive") : t("driverSubscription.statusInactive")}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-xl border border-slate-700/70 bg-slate-900/45 p-3">
                <div className="text-xs text-slate-400">{t("driverSubscription.priceLabel")}</div>
                <div className="mt-1 flex flex-wrap items-baseline gap-2">
                  <span className="text-xs font-bold text-slate-500 line-through">
                    {subscriptionPromo.oldLabel}
                  </span>
                  <span className="font-black">
                    {baseTariff?.price?.label || subscriptionPrice?.label || "—"}
                  </span>
                  <span className="rounded-full border border-emerald-500/40 bg-emerald-900/30 px-2 py-0.5 text-[11px] font-black text-emerald-200">
                    {subscriptionPromo.discountLabel}
                  </span>
                </div>
              </div>
              <div className="rounded-xl border border-slate-700/70 bg-slate-900/45 p-3">
                <div className="text-xs text-slate-400">{t("driverSubscription.untilLabel")}</div>
                <div className="mt-1 font-black">{fmtDateOnly(baseTariff?.until)}</div>
              </div>
            </div>
            <button
                type="button"
                onClick={() => startSubscriptionPayment("driver_base_access_30d")}
                disabled={subscriptionSubmitting}
                className={`mt-4 w-full rounded-2xl px-4 py-3 text-base font-black text-white ${
                  subscriptionSubmitting
                    ? "bg-slate-700 opacity-70"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {subscriptionSubmitting
                  ? t("driverSubscription.paymentPreparing", { defaultValue: "Подготовка оплаты..." })
                  : baseTariff?.active
                    ? t("driverSubscription.renewAccess", { defaultValue: "Продлить доступ" })
                    : t("driverSubscription.payNow", { defaultValue: "Перейти к оплате" })}
              </button>
          </div>

          {/* REISOR_DRIVER_LEGACY_CONTACTS_UI_HIDDEN_V1:
              legacy contacts add-on is intentionally not rendered. */}
        </div>


      </div>

        {/* REISOR_DRIVER_PUBLIC_BILLING_CLEAN_V1
            Legacy wallet, topup, currency display and transaction history UI
            are hidden from the public driver app. Backend, models and
            SmartPanel logic remain untouched for future use. */}


      <div className="h-6" />
    </div>
  );
}
